import { useEffect, useState } from "react";
import { fetchAlerts, acknowledgeAlert, fetchDomains, fetchClients, ApiError } from "../api/client.js";
import type { DomainView, ClientView } from "@atlas/shared/types";

interface AlertEntry {
  id: number;
  domainId: number;
  alertType: string;
  sentAt: string;
  channel: string;
  acknowledged: boolean;
}

function alertTypeColor(type: string): string {
  if (type.includes("1D")) return "text-red-400";
  if (type.includes("7D") || type.includes("14D")) return "text-amber-400";
  if (type === "REVOKED") return "text-red-500";
  if (type === "SCAN_FAIL") return "text-orange-400";
  return "text-gray-400";
}

// ─── iCal generation ─────────────────────────────────────────────────────────

function toIcalDate(date: Date): string {
  // Format as YYYYMMDD (all-day event)
  return date.toISOString().slice(0, 10).replace(/-/g, "");
}

function escapeIcal(s: string): string {
  return s.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\n/g, "\\n");
}

function generateIcal(domains: DomainView[], clientMap: Map<number, string>): string {
  const lines: string[] = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//ATLAS TLS Monitor//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
  ];

  const thresholds: Array<{ days: number; label: string }> = [
    { days: 90, label: "90D" },
    { days: 60, label: "60D" },
    { days: 30, label: "30D" },
  ];

  for (const domain of domains) {
    if (!domain.latestSnapshot) continue;
    const notAfter = new Date(domain.latestSnapshot.notAfter);
    if (isNaN(notAfter.getTime())) continue;

    const clientName = clientMap.get(domain.clientId) ?? `Client${domain.clientId}`;
    const safeDomain = domain.hostname.replace(/[^a-zA-Z0-9.-]/g, "");

    for (const { days, label } of thresholds) {
      const eventDate = new Date(notAfter.getTime() - days * 86_400_000);
      const title = `[${label}]-${clientName}-${domain.hostname}`;
      const uid = `atlas-${label.toLowerCase()}-${domain.id}-${safeDomain}@atlas`;

      lines.push(
        "BEGIN:VEVENT",
        `UID:${uid}`,
        `DTSTART;VALUE=DATE:${toIcalDate(eventDate)}`,
        `DTEND;VALUE=DATE:${toIcalDate(new Date(eventDate.getTime() + 86_400_000))}`,
        `SUMMARY:${escapeIcal(title)}`,
        `DESCRIPTION:${escapeIcal(
          `Certificate for ${domain.hostname} expires on ${notAfter.toISOString().slice(0, 10)} (${days} days from today).`
        )}`,
        `CATEGORIES:TLS,CERTIFICATE`,
        "END:VEVENT"
      );
    }
  }

  lines.push("END:VCALENDAR");
  return lines.join("\r\n");
}

function downloadIcal(content: string): void {
  const blob = new Blob([content], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "atlas-cert-expirations.ics";
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function AlertsPage() {
  const [alerts, setAlerts] = useState<AlertEntry[]>([]);
  const [domains, setDomains] = useState<DomainView[]>([]);
  const [clientMap, setClientMap] = useState<Map<number, string>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([fetchAlerts(), fetchDomains(), fetchClients()])
      .then(([alertData, domainData, clientData]) => {
        setAlerts(alertData as AlertEntry[]);
        setDomains(domainData);
        const map = new Map<number, string>();
        (clientData as ClientView[]).forEach((c) => map.set(c.id, c.name));
        setClientMap(map);
      })
      .catch((e: unknown) => {
        setError(e instanceof ApiError ? e.message : "Failed to load data");
      })
      .finally(() => setLoading(false));
  }, []);

  async function handleAck(id: number) {
    try {
      await acknowledgeAlert(id);
      setAlerts((prev) =>
        prev.map((a) => (a.id === id ? { ...a, acknowledged: true } : a))
      );
    } catch {
      // Non-critical — don't break the UI
    }
  }

  function handleExportIcal() {
    const ical = generateIcal(domains, clientMap);
    downloadIcal(ical);
  }

  const expiringCount = domains.filter(
    (d) => d.latestSnapshot && d.latestSnapshot.daysRemaining > 0 && d.latestSnapshot.daysRemaining <= 90
  ).length;

  if (loading) return <div className="text-gray-500 text-sm">Loading…</div>;
  if (error) return <div className="text-red-400 text-sm">Error: {error}</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Alert Log</h1>
        <button
          onClick={handleExportIcal}
          title={
            expiringCount > 0
              ? `Export iCal reminders for ${expiringCount} expiring cert${expiringCount === 1 ? "" : "s"}`
              : "Export iCal reminders for all tracked certs"
          }
          className="text-xs px-3 py-1.5 rounded bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors flex items-center gap-1.5"
        >
          <span>&#x1F4C5;</span>
          Export iCal
          {expiringCount > 0 && (
            <span className="ml-1 bg-amber-700 text-amber-200 rounded px-1 py-0.5 text-xs leading-none">
              {expiringCount}
            </span>
          )}
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left text-gray-500 border-b border-gray-800">
              <th className="pb-2 pr-4">Type</th>
              <th className="pb-2 pr-4">Domain</th>
              <th className="pb-2 pr-4">Sent</th>
              <th className="pb-2 pr-4">Channel</th>
              <th className="pb-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {alerts.map((a) => (
              <tr
                key={a.id}
                className={`border-b border-gray-900 ${a.acknowledged ? "opacity-50" : ""}`}
              >
                <td className={`py-2 pr-4 font-mono text-xs ${alertTypeColor(a.alertType)}`}>
                  {a.alertType}
                </td>
                <td className="py-2 pr-4 text-gray-400">
                  {domains.find((d) => d.id === a.domainId)?.hostname ?? `#${a.domainId}`}
                </td>
                <td className="py-2 pr-4 text-gray-500 text-xs">
                  {new Date(a.sentAt).toLocaleString()}
                </td>
                <td className="py-2 pr-4 text-gray-500 text-xs">{a.channel}</td>
                <td className="py-2">
                  {a.acknowledged ? (
                    <span className="text-gray-600 text-xs">Acknowledged</span>
                  ) : (
                    <button
                      onClick={() => handleAck(a.id)}
                      className="text-xs text-blue-400 hover:text-blue-300 underline"
                    >
                      Acknowledge
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {alerts.length === 0 && (
              <tr>
                <td colSpan={5} className="py-8 text-center text-gray-600">
                  No alerts yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
